import React, { useMemo, useState } from "react";
import { FiX } from "react-icons/fi";
import PlanFunctionsMultiSelect from "components/PlanFunctionsMultiSelect/PlanFunctionsMultiSelect";
import { withTranslation } from "react-i18next";
import "./GroupsMultiSelect.scss";

function normalizeIds(value) {
	if (!Array.isArray(value)) return [];
	return value.map((id) => String(id)).filter(Boolean);
}

/**
 * Selector de grupos para multigrupos: multi-select + botón añadir + chips.
 * El desplegable acumula varios grupos; al pulsar Añadir pasan a la lista de chips.
 *
 * @param {Array}  groups — lista `{ id, name }`.
 * @param {Array}  value  — ids ya añadidos (chips).
 * @param {function} onChange — `(ids: string[]) => void`.
 */
const GroupsMultiSelect = ({
	groups = [],
	value,
	onChange,
	t,
	placeholder,
	className = "",
	disabled = false,
	controlId,
	isInvalid = false,
}) => {
	const [pendingIds, setPendingIds] = useState([]);
	const [addError, setAddError] = useState(false);

	const committedIds = useMemo(() => new Set(normalizeIds(value)), [value]);

	const groupsById = useMemo(() => {
		const map = new Map();
		(groups || []).forEach((group) => {
			if (group && group.id != null) {
				map.set(String(group.id), group);
			}
		});
		return map;
	}, [groups]);

	const availableGroups = useMemo(
		() =>
			(groups || []).filter((group) => !committedIds.has(String(group.id))),
		[groups, committedIds]
	);

	const committedGroups = useMemo(
		() =>
			normalizeIds(value)
				.map((id) => groupsById.get(id))
				.filter(Boolean),
		[value, groupsById]
	);

	const handlePendingChange = (ids) => {
		setPendingIds(ids);
		if (ids.length > 0) {
			setAddError(false);
		}
	};

	const handleAdd = () => {
		if (!pendingIds.length) {
			setAddError(true);
			return;
		}

		const merged = [...committedIds];
		pendingIds.forEach((id) => {
			const idStr = String(id);
			if (!merged.includes(idStr)) {
				merged.push(idStr);
			}
		});

		onChange(merged);
		setPendingIds([]);
		setAddError(false);
	};

	const handleRemove = (id) => {
		const idStr = String(id);
		onChange(normalizeIds(value).filter((item) => item !== idStr));
	};

	const showAddError = addError || isInvalid;

	return (
		<div
			className={`groups-multiselect-field ${className}`.trim()}
			data-invalid={showAddError ? "true" : undefined}
		>
			<div className="flex-group mb15 groups-multiselect-add">
				<PlanFunctionsMultiSelect
					functions={availableGroups}
					value={pendingIds}
					onChange={handlePendingChange}
					placeholder={placeholder || t("groups.form.selectGroups")}
					allLabel={t("groups.form.selectGroups")}
					searchPlaceholder={t("groups.form.searchGroup")}
					clearLabel={t("groups.form.clearGroups")}
					noResultsLabel={t("groups.form.noGroupsFound")}
					selectedCountLabel={t("groups.form.selectedGroupsCount")}
					className="groups-multiselect"
					disabled={disabled || availableGroups.length === 0}
					controlId={controlId}
				/>
				<button
					type="button"
					onClick={handleAdd}
					className="btn btn-primary"
					disabled={disabled || availableGroups.length === 0}
				>
					{t("common.add")}
				</button>
			</div>

			{showAddError && committedGroups.length === 0 && (
				<div className="invalid-feedback d-block mb10">
					{t("groups.form.mustAddGroup")}
				</div>
			)}

			{committedGroups.length > 0 && (
				<ul className="list-tags">
					{committedGroups.map((item) => (
						<li key={item.id} className="tag-default tag-delete">
							{item.name}
							<button
								type="button"
								onClick={() => handleRemove(item.id)}
								className="btn-tag-delete btn-transparent"
								disabled={disabled}
								aria-label={t("common.delete")}
							>
								<FiX />
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
};

export default withTranslation()(GroupsMultiSelect);
